import React from 'react';
import { Button, Card, Col, Row, Space } from 'antd';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import TableBase from 'common/components/table/table-base';
import integrationEventsService from 'features/eiq-manage/service/integration-events-service/integration-events.service';
import { integrationEventsTableInitialColumns } from './integration-events.config';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { ColumnFilter } from 'common/components/table/filters/column-filter/column-filter';
import { IFilterConfig } from 'common/components/table/filters/column-filter/column-filter.types';
import { operationTypeOptions, syncStatusOptions } from 'features/eiq-manage/enums/integration-event.enum';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import { ReactComponent as DeleteIcon } from 'assets/icons/delete.svg';
import { useModal } from 'common/hooks/use-modal/use-modal.hook';
import { IIntegrationEvent } from 'features/eiq-manage/service/integration-events-service/integration-events.service.types';
import { IConfirmDeleteModalProps } from 'common/components/modal/confirm-delete-modal/confirm-delete-modal.types';
import { trackPromise } from 'react-promise-tracker';
import ConfirmDeleteModal from 'common/components/modal/confirm-delete-modal/confirm-delete-modal';

const IntegrationEvents = () => {
  const tableControls = useTableControls({
    onFetch: integrationEventsService.getAll,
    initialColumns: integrationEventsTableInitialColumns,
  });

  const deleteModal = useModal({
    onBeforeShow: (setModalInitData) => {
      const rows = tableControls.getSelectedRows();

      if (!rows.length) {
        return;
      }

      const ids: string[] = rows.map((i: IIntegrationEvent) => i.id);

      const data: IConfirmDeleteModalProps = {
        message: 'Are you sure you want to delete selected integration events?',
        onDelete: () => {
          integrationEventsService.delete(ids).then(() => {
            deleteModal.hideModal(true);
          });
        },
      };

      setModalInitData(data);
    },
    onClose: (isSuccess: boolean | undefined) => {
      if (isSuccess) {
        tableControls.setSelectedRow(null);
        tableControls.refetchData();
      }
    },
  });

  const onRetryClicked = async () => {
    const rows = tableControls.getSelectedRows();

    if (!rows.length) {
      return;
    }

    const ids: string[] = rows.map((i: IIntegrationEvent) => i.id);

    await trackPromise(integrationEventsService.setToRetry(ids));
    tableControls.setSelectedRow(null);
    tableControls.refetchData();
  };

  return (
    <Card className="eiq-card eiq-card-border eiq-configuration">
      <Row className="eiq-card-header">
        <Col span={24}>
          <h1 className="h1">Integration events management</h1>
        </Col>
      </Row>
      <TableContextProvider controls={tableControls}>
        <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
          <Col flex="auto">
            <SearchFilter
              adaptiveSearch={true}
              enableProgressiveSearch={true}
              placeholder="Search by integration type, group key"
              rulesForColumn={{
                integrationType: FilterTypesEnum.StartWith,
                groupKey: FilterTypesEnum.StartWith,
              }}
              rightSide={
                <Space size={8}>
                  <ColumnVisibility isColumnReorderAllow={true} />
                  <ColumnFilter filterConfig={incidentReportListFilter} />
                  <Button
                    icon={<DeleteIcon stroke="#505762" />}
                    size="middle"
                    onClick={deleteModal.showModal}
                    disabled={!tableControls.getSelectedRows().length}>
                    Delete
                  </Button>
                  <Button size="middle" onClick={onRetryClicked} disabled={!tableControls.getSelectedRows().length}>
                    Set to Retry
                  </Button>
                </Space>
              }
            />
          </Col>
        </Row>
        <TableBase selectFirstRowByDefault={false} selectableWithCheckboxes />
      </TableContextProvider>
      <ConfirmDeleteModal
        isEdit={false}
        initData={deleteModal?.initData}
        title="Delete"
        isOpen={deleteModal?.isOpenModal}
        onClose={deleteModal?.hideModal}
      />
    </Card>
  );
};

export default IntegrationEvents;

export const incidentReportListFilter: IFilterConfig[] = [
  {
    name: 'operation',
    filterType: FilterTypesEnum.Equals,
    label: 'Operation',
    type: {
      options: operationTypeOptions,
      type: 'SELECT',
    },
  },
  {
    name: 'status',
    filterType: FilterTypesEnum.Equals,
    label: 'Status',
    type: {
      options: syncStatusOptions,
      type: 'SELECT',
    },
  },
];
